import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "../api";
import { StatusBadge, VerificationBadge, type PublicVerification } from "../components/StatusBadge";
import { isPending } from "../components/Field";
import { STATES } from "../states";
import { useAuth } from "../auth";
import { useCallerState } from "../callerState";
import { useLinkedCallerState } from "../linkedCallerState";

export type DirectorySearch = { category?: string; department?: string; state?: string; jurisdiction?: string; q?: string };
type Card = { id: string; slug: string; name: string; summary: unknown; status: string; jurisdiction: string;
  category_name: string; department_acronym: string | null; has_state: boolean; verification: PublicVerification };
type Cat = { slug: string; name: string; children: { slug: string; name: string }[] };

export function Directory() {
  const { me } = useAuth();
  const search = useSearch({ from: "/app/directory" });
  const navigate = useNavigate({ from: "/directory" });
  const set = (patch: Partial<DirectorySearch>) => navigate({ search: (s: DirectorySearch) => ({ ...s, ...patch }) });
  const caller = useCallerState();
  // The caller's state (the top bar's) filters the list and goes with the program link, so the program page shows
  // that state's details; a state in the address becomes the caller's on arrival, and the address follows, replaced.
  const state = useLinkedCallerState(search.state,
    (s) => navigate({ search: (prev: DirectorySearch) => ({ ...prev, state: s }), replace: true }));
  const [q, setQ] = useState(search.q ?? "");
  useEffect(() => { const t = setTimeout(() => set({ q: q || undefined }), 250); return () => clearTimeout(t); }, [q]);

  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api<{ categories: Cat[] }>("/api/directory/categories") });
  const filters: DirectorySearch = { ...search, state };
  const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v) as [string, string][]).toString();
  const list = useQuery({ queryKey: ["programs", qs], queryFn: () => api<{ programs: Card[] }>(`/api/directory/programs?${qs}`) });

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Program directory</h1>
        {me?.user.role === "admin" && <Link to="/admin/programs/$id" params={{ id: "new" }} className="btn-primary">New program</Link>}
      </div>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div><label className="label" htmlFor="q">Search by name</label>
          <input id="q" className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. rental" /></div>
        <div><label className="label" htmlFor="cat">Category</label>
          <select id="cat" className="input" value={search.category ?? ""} onChange={(e) => set({ category: e.target.value || undefined })}>
            <option value="">All categories</option>
            {cats.data?.categories.map((c) => (
              <optgroup key={c.slug} label={c.name}>
                <option value={c.slug}>All {c.name}</option>
                {c.children.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </optgroup>
            ))}
          </select></div>
        {/* The caller's state, not a filter of its own: the top bar shows the same and follows it. Named "State shown"
            so that only the top bar's control is "Caller's state". */}
        <div><label className="label" htmlFor="state">State shown</label>
          <select id="state" className="input" value={state ?? ""} onChange={(e) => caller.setState(e.target.value || undefined)}>
            <option value="">Any state</option>
            {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select></div>
        <div><label className="label" htmlFor="jur">Level</label>
          <select id="jur" className="input" value={search.jurisdiction ?? ""} onChange={(e) => set({ jurisdiction: e.target.value || undefined })}>
            <option value="">All levels</option><option value="federal">Federal</option><option value="state">State</option><option value="local">Local</option>
          </select></div>
      </div>

      {list.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {list.error && <p role="alert" className="text-sm text-red-600">Could not load programs.</p>}
      {list.data?.programs.length === 0 && <p className="text-sm text-slate-500">No programs match these filters.</p>}
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {list.data?.programs.map((p) => (
          <li key={p.id} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="min-w-0 flex-1">
              <Link to="/directory/programs/$slug" params={{ slug: p.slug }} search={{ state }}
                className="font-medium text-brand-700 hover:underline dark:text-brand-100">{p.name}</Link>
              <div className="truncate text-xs text-slate-500">
                {p.category_name}{p.department_acronym ? ` · ${p.department_acronym}` : ""} · {p.jurisdiction}
                {p.has_state && <span className="ml-1 text-brand-600">· {state} details</span>}
              </div>
              {!isPending(p.summary) && typeof p.summary === "string" && <p className="mt-0.5 line-clamp-1 text-sm text-slate-600 dark:text-slate-400">{p.summary}</p>}
            </div>
            <div className="flex items-center gap-3"><StatusBadge status={p.status} /><VerificationBadge v={p.verification} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
}
